import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import HeroContext from "../contexts/HeroContext";
import SettingsContext from "../contexts/SettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { generateMapData, findStartingTown } from "../utils/mapGenerator";
import WorldMapDisplay from "../components/WorldMapDisplay";
import OnboardingSteps from "../components/OnboardingSteps";
import { sendEvent } from "../services/telemetry";
import SegmentedControl from "../components/SegmentedControl";
import "../styles/redesign.css";
import RaritySelect from "../components/RaritySelect";
import { storyTemplates } from "../data/storyTemplates";
import { launchCampaign, mergeLocationNames } from "../game/campaignLauncher";
import { createLogger } from "../utils/logger";
import { QUEST_ENEMIES, getEnemiesByTierAndTheme } from "../data/questEnemies";
import { QUEST_BUILDINGS, NPC_ROLES, POI_TYPES, THEME_DEFAULTS, THEME_NAMES, getCustomQuestItems } from "../data/questPickerData";
import { isPremium, hasTier, isThemePremium, isTemplatePremium, canUseTemplate, canUseTheme } from "../game/entitlements";
import { TIER_LEVEL_BANDS, tierBandLabel } from "../game/templateSections";
import { getStoryArcs, getArcSections, healTeaserChapter, TEASER_SIGN_IN_COPY } from "../game/storyArcs";
import ArcDetailModal, { chapterChip } from "../components/ArcDetailModal";
import { isOpeningAccessible } from "../game/campaignChain";
import { resolveWaterTownAccess, waterTownWorldGenOptions } from "../game/waterTowns";
import { normalizeWorldSeed } from "../utils/worldSeed";

const logger = createLogger('new-game');

const NewGame = () => {

  // characters should be saved in Context
  const { heroes } = useContext(HeroContext);
  // Get settings, provider, and model state from context
  const { settings, setSettings } = useContext(SettingsContext);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Premium entitlement (placeholder — false for free tier by default; honours the
  // localStorage dev override in entitlements.js so premium content can be tested).
  const premiumUnlocked = isPremium();

  // Water towns (#65): what this tier's new worlds will include. Also drives the
  // manual map preview's world-gen options so preview and launch build the same
  // world. Free tier gets no note and no options (absent, not locked: decision 13).
  const waterTownAccess = resolveWaterTownAccess();
  const waterTownNote = waterTownAccess.allowCanal
    ? 'Your realm will include a canal city where its great river meets the sea, and some river settlements will grow into island-district river towns.'
    : waterTownAccess.allowRiverfork
      ? 'Your realm\'s great river runs to the sea, and some river settlements will grow into island-district river towns.'
      : null;

  // Clear any stale session ID when starting a new game
  useEffect(() => {
    localStorage.removeItem('activeGameSessionId');
  }, []);

  // Existing state
  const [shortDescription, setShortDescription] = useState(settings?.shortDescription || '');
  const [grimnessLevel, setGrimnessLevel] = useState(settings?.grimnessLevel || '');
  const [darknessLevel, setDarknessLevel] = useState(settings?.darknessLevel || '');

  // New state for additional settings
  const [magicLevel, setMagicLevel] = useState(settings?.magicLevel || 'Low Magic'); // Default example
  const [technologyLevel, setTechnologyLevel] = useState(settings?.technologyLevel || 'Medieval'); // Default example
  const [responseVerbosity, setResponseVerbosity] = useState(settings?.responseVerbosity || 'Moderate'); // Default example
  // Which tone dials the player has manually set. A theme change fills only the dials they
  // haven't touched, so picking a theme gives coherent defaults without clobbering choices.
  const [toneTouched, setToneTouched] = useState({});
  const [campaignGoal, setCampaignGoal] = useState(settings?.campaignGoal || '');
  const [milestones, setMilestones] = useState(() => {
    if (!settings?.milestones || settings.milestones.length === 0) return [];
    if (typeof settings.milestones[0] === 'object') return settings.milestones;
    return settings.milestones.map(text => ({ text, location: null }));
  });

  // State for validation error message
  const [formError, setFormError] = useState('');

  // State for generated map
  const [generatedMap, setGeneratedMap] = useState(null);
  const [worldSeed, setWorldSeed] = useState(settings?.worldSeed || null);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Display name of the chosen story, captured at selection time so it survives
  // selectedTemplate being reset to 'custom' when the player visits other tabs.
  const [templateLabel, setTemplateLabel] = useState(null);
  const [customNames, setCustomNames] = useState({ towns: [], mountains: [] });
  // World biome theme for the generated map (Phase 2b). 'grassland' (default) reproduces
  // the historical plains map; a themed template (e.g. desert) overrides it.
  const [worldTheme, setWorldTheme] = useState('grassland');



  // Possible options
  const grimnessOptions = ['Noble', 'Neutral', 'Bleak', 'Grim'];
  const darknessOptions = ['Bright', 'Neutral', 'Grey', 'Dark'];

  const magicOptions = ['No Magic', 'Low Magic', 'High Magic', 'Arcane Tech'];
  const technologyOptions = ['Ancient', 'Medieval', 'Renaissance', 'Industrial']; // Excluded 'Futuristic'
  const verbosityOptions = ['Concise', 'Moderate', 'Descriptive'];


  const applyTemplate = (template) => {
    setSelectedTemplate(template.id);
    setTemplateLabel(template.subtitle ? `${template.name} — ${template.subtitle}` : template.name);
    setShortDescription(template.settings.shortDescription);
    setGrimnessLevel(template.settings.grimnessLevel);
    setDarknessLevel(template.settings.darknessLevel);
    setMagicLevel(template.settings.magicLevel);
    setTechnologyLevel(template.settings.technologyLevel);
    setResponseVerbosity(template.settings.responseVerbosity);
    setToneTouched({}); // a picked template is a fresh tone baseline
    setCampaignGoal(template.settings.campaignGoal || '');
    setMilestones(template.settings.milestones || []);
    setCustomNames(template.customNames || { towns: [], mountains: [] });
    setWorldTheme(template.settings.theme || 'grassland');
    // A previously generated preview map belongs to the OLD customNames/theme; keeping
    // it would launch the campaign on a world without its milestone towns (the missing
    // Hearthmere trading post, playtest 2026-07-07). Drop it; the seed is kept so
    // "Build Map from Seed" reproduces the same world under the new template's names.
    // launchCampaign also guards against incompatible provided maps as a backstop.
    setGeneratedMap(null);
    setShowMapPreview(false);
  };

  // Preselect a template when handed one via navigation state (the "Continue your
  // legend" picker's "Start as a New Game" path for geography-incompatible
  // campaigns). One-shot on mount; the player can still change their pick.
  const { state: navState } = useLocation();
  useEffect(() => {
    const preselectId = navState?.preselectTemplateId;
    if (!preselectId) return;
    const template = storyTemplates.find((t) => t.id === preselectId && !t.comingSoon);
    // Entitlement check (maintainer 2026-07-06): the preselect path used to
    // apply ANY template, so a free account handed a premium preselect id got
    // the desert/snow theme applied to state (the historic "free player
    // generates a sand map via the custom tab" hole). Locked templates now
    // nudge instead of applying.
    if (template && !canUseTemplate(template)) {
      setFormError('That adventure is part of Membership. Open the Membership page to unlock it, or pick a free adventure below.');
      return;
    }
    // Teaser stubs have no settings to apply (shop-window card faces only);
    // applying one would crash. The continue-legend picker filters them out,
    // but never trust the navigation state: explain instead of applying.
    if (template && template.teaser === true) {
      setFormError(TEASER_SIGN_IN_COPY);
      return;
    }
    if (template) applyTemplate(template);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Last played" default (#88): with no navigation preselect, quietly preselect
  // the template from the previous launch. Same guards as above, but silent —
  // a locked/teaser/missing remembered template just means no preselect (e.g. a
  // server-delivered premium template not registered this session).
  const DEFAULT_TEMPLATE_ID = 'heroic-fantasy-t1';
  const [lastPlayedTemplateId] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dungeongpt:lastLaunch') || 'null')?.templateId || null;
    } catch (e) {
      return null;
    }
  });
  // With no usable last-played template (e.g. a first visit), fall back to the
  // starter campaign so the page opens with a story already picked. Skipped when
  // returning with story text in settings, so a custom tale isn't overwritten.
  useEffect(() => {
    if (navState?.preselectTemplateId) return;
    const usable = (id) => {
      const template = id && storyTemplates.find((t) => t.id === id && !t.comingSoon);
      return template && template.teaser !== true && canUseTemplate(template) ? template : null;
    };
    const template = usable(lastPlayedTemplateId)
      || (!settings?.shortDescription && usable(DEFAULT_TEMPLATE_ID));
    if (template) applyTemplate(template);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleSubmit = () => {
    // Custom tab: validate slots and auto-generate description if needed
    if (activeTab === 'custom') {
      const slotError = validateCustomSlots();
      if (slotError) {
        setFormError(slotError);
        return;
      }

      // Auto-generate shortDescription from slot selections if empty
      if (!shortDescription.trim()) {
        const themeName = THEME_DEFAULTS[customTheme]?.name || 'Fantasy';
        const parts = [];
        if (slot1Item) {
          const item = availableQuestItems.find(i => i.id === slot1Item);
          if (item) parts.push(`seek the ${item.name}`);
        }
        if (slot4Enemy) {
          const enemy = QUEST_ENEMIES[slot4Enemy];
          if (enemy) parts.push(`defeat ${enemy.name}`);
        }
        const desc = parts.length > 0
          ? `A ${themeName.toLowerCase()} adventure where heroes ${parts.join(' and ')}.`
          : `A custom ${themeName.toLowerCase()} adventure.`;
        setShortDescription(desc);
      }
    }

    // Shared validation
    if (!shortDescription.trim() && activeTab !== 'custom') {
      setFormError(
        activeTab === 'templates'
          ? 'Please select a story to begin.'
          : 'Please enter a story description.'
      );
      return;
    }
    if (!grimnessLevel) {
      setFormError('Please select a Grimness level.');
      return;
    }
    if (!darknessLevel) {
      setFormError('Please select a Darkness level.');
      return;
    }
    setFormError('');

    // Premium backstop: no matter which path set it, a user must never start an
    // adventure their tier can't use (canUseTemplate also honours per-template
    // minTier), and a free user must never generate a sand/snow (premium) world
    // map. This closes loopholes like a premium biome theme carried over from a
    // previewed template into Custom/Freeform.
    const selTpl = selectedTemplate && selectedTemplate !== 'custom'
      ? storyTemplates.find(t => t.id === selectedTemplate)
      : null;
    if (selTpl && !canUseTemplate(selTpl)) {
      setFormError('This is a Members adventure. Open the Membership page to unlock it, or pick a free adventure to begin.');
      return;
    }
    if (selTpl && selTpl.teaser === true) {
      // Teaser stub reached submit: entitled tier but the server delivery has
      // not replaced the stub this session; there is no campaign to launch.
      setFormError('This adventure loads with your account content at sign-in. Sign out and back in (or refresh) and it will be playable.');
      return;
    }
    if (!premiumUnlocked && isThemePremium(worldTheme)) {
      setFormError('Desert and snow adventures are a Members feature. Open the Membership page to unlock them, or choose a temperate setting to continue.');
      return;
    }

    const templateName = templateLabel
      || (selectedTemplate === 'custom' || !selectedTemplate ? 'Custom Tale'
        : storyTemplates.find(t => t.id === selectedTemplate)?.name || 'Unknown Template');

    // For custom tab, use the auto-generated description if it was just set
    const finalDescription = shortDescription.trim() ||
      `A custom ${(THEME_DEFAULTS[customTheme]?.name || 'fantasy').toLowerCase()} adventure.`;

    // Resolve tier and level range from template or custom settings
    const templateData = selectedTemplate && selectedTemplate !== 'custom'
      ? storyTemplates.find(t => t.id === selectedTemplate)
      : null;

    // Run the shared campaign-start pipeline (map gen, milestone spawning, town
    // pre-generation, side-quest selection, settings snapshot). Shared with the
    // quest-chaining flow; see src/game/campaignLauncher.js.
    let launch;
    try {
      launch = launchCampaign({
        templateId: templateData?.id || null,
        templateName,
        shortDescription: finalDescription,
        grimnessLevel,
        darknessLevel,
        magicLevel,
        technologyLevel,
        responseVerbosity,
        campaignGoal,
        milestones,
        customNames,
        worldTheme,
        tier: templateData?.tier || customTier || 1,
        levelRange: templateData?.levelRange || null,
        premium: isTemplatePremium(templateData),
      }, {
        // Keep the manual preview map (Custom/Freeform) when one was built;
        // Ready-Made adventures generate here.
        seed: normalizeWorldSeed(worldSeed),
        mapData: generatedMap || null,
      });
    } catch (err) {
      logger.error('Campaign launch failed:', err);
      setFormError(err.message);
      return;
    }

    // Fresh game session ID for this new game
    localStorage.setItem('activeGameSessionId', launch.gameSessionId);

    sendEvent('game_launched', {
      templateId: selectedTemplate === 'custom' ? 'custom' : selectedTemplate,
      guest: !user,
    });

    // Remember the launched template so the next New Game preselects it ("Last
    // played"). Custom games aren't remembered: their slot state isn't restorable
    // from an id. Guarded: storage can throw in private mode.
    if (selectedTemplate && selectedTemplate !== 'custom') {
      try {
        localStorage.setItem('dungeongpt:lastLaunch', JSON.stringify({ templateId: selectedTemplate }));
      } catch (e) { /* ignore */ }
    }

    // Persist the launch snapshot so a hard reload before the first save cannot lose
    // the campaign settings (milestones/story/goal/tone). The generated map already
    // survives a reload because it rides router navigation state; settings otherwise
    // live only in the in-memory SettingsContext and reset to {} on reload. Game.js
    // rehydrates from this key when the context is empty and there is no saved row
    // yet. Prune any earlier launch snapshots first so only the most recent unsaved
    // launch is ever kept (bounds sessionStorage growth). All access is guarded since
    // storage can throw in private mode.
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('dgpt:launchSettings:')) sessionStorage.removeItem(k);
      }
      sessionStorage.setItem(
        `dgpt:launchSettings:${launch.gameSessionId}`,
        JSON.stringify(launch.settings)
      );
    } catch (e) {
      logger.warn('Could not persist launch settings snapshot', e);
    }

    setSettings(launch.settings);
    navigate('/hero-selection', { state: { heroes, generatedMap: launch.mapData, worldSeed: launch.worldSeed, gameSessionId: launch.gameSessionId, townMapsCache: launch.townMapsCache } });
  };

  // Ready-Made "Begin This Campaign": applyTemplate's state updates (selected
  // template, tone, milestones, theme) must flush before handleSubmit reads
  // them. The modal's Begin button applies the template and raises this flag;
  // we launch from an effect on the next render, when handleSubmit closes over
  // the freshly-applied state (a direct call would use the pre-apply closure).
  const [pendingCampaignLaunch, setPendingCampaignLaunch] = useState(false);
  useEffect(() => {
    if (!pendingCampaignLaunch) return;
    setPendingCampaignLaunch(false);
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCampaignLaunch]);

  // Tab state
  const [activeTab, setActiveTab] = useState('templates');

  // Arc detail modal (#73 phase 1): which arc's chapter ladder is open, which
  // chapter row is expanded, plus the in-modal notice line and the teaser
  // self-heal loading marker (maintainer ruling A, 2026-07-07).
  const [previewArcId, setPreviewArcId] = useState(null);
  const [previewChapterId, setPreviewChapterId] = useState(null);
  const [arcModalNotice, setArcModalNotice] = useState(null);
  const [retryingChapterId, setRetryingChapterId] = useState(null);

  // Party max level for level warnings on templates
  const partyMaxLevel = heroes.length > 0
    ? Math.max(...heroes.map(h => h.heroLevel || 1))
    : 0;

  const milestoneTypeIcon = {
    item: '📦',
    combat: '⚔️',
    location: '📍',
    narrative: '💬',
  };

  // ── Arc grouping (#73 phase 1, docs/ARC_CARDS_AND_NARRATIVE_PLAN.md) ──────
  // One card per campaign ARC (theme), derived from the LIVE storyTemplates
  // array every render: server-delivered chapters, teaser stubs turning
  // playable, and brand-new themes all flow in mid-session (AuthContext's
  // premiumContentReady flip re-renders this page). The old Seasoned Parties /
  // Legendary Campaigns sections are absorbed by the detail modal's chapter
  // picker (decision 3); templateSections.js remains for other consumers.
  const arcs = getStoryArcs(storyTemplates);
  const arcSections = getArcSections(arcs);
  const previewArc = previewArcId ? arcs.find((a) => a.id === previewArcId) : null;

  const openArcModal = (arc) => {
    setArcModalNotice(null);
    const defaultChapter = arc.chapters.find((c) => c.startable) || arc.chapters[0];
    setPreviewChapterId(defaultChapter?.id || null);
    setPreviewArcId(arc.id);
  };

  const closeArcModal = () => {
    setPreviewArcId(null);
    setPreviewChapterId(null);
    setArcModalNotice(null);
  };

  const lockedChapterCopy = (chapter) =>
    chapter.gateTier === 'premium' || chapter.gateTier === 'elite'
      ? 'This chapter is a Premium unlock (coming soon). Preview the arc freely; a Premium account is needed to play it.'
      : 'This chapter comes with Membership. Open the Membership page to unlock it, or pick a free chapter to begin.';

  // Teaser self-heal (maintainer ruling A, 2026-07-07): a teaser-stub chapter
  // click never dead-ends. Signed out, the fix IS signing in (delivery
  // requires auth), so say that. Signed in, retry the delivery: if the stub
  // was replaced, apply the now-playable chapter; only if it is STILL a
  // teaser show the explanation. The clicked row shows a loading state while
  // the retry is in flight.
  const handleTeaserChapter = async (chapter, { fromModal = false } = {}) => {
    const notify = fromModal ? setArcModalNotice : setFormError;
    if (retryingChapterId) return;
    if (!user) {
      notify(TEASER_SIGN_IN_COPY);
      return;
    }
    notify('');
    setRetryingChapterId(chapter.id);
    let result;
    try {
      result = await healTeaserChapter(chapter.template, { isSignedIn: true });
    } finally {
      setRetryingChapterId(null);
    }
    if (result?.status === 'delivered') {
      applyTemplate(result.template);
      if (fromModal) setPreviewChapterId(result.template.id);
    } else if (result) {
      notify(result.message);
    }
  };

  // Clicking an arc card begins its entry chapter (the arc card is
  // presentation, not a new save type: decision 2). Locked entries explain,
  // teaser entries self-heal, comingSoon entries are inert.
  const handleArcCardClick = (arc) => {
    const entry = arc.chapters[0];
    if (entry.comingSoon) return;
    if (entry.locked) {
      setFormError(lockedChapterCopy(entry));
      return;
    }
    if (entry.teaser) {
      handleTeaserChapter(entry);
      return;
    }
    applyTemplate(entry.template);
  };

  const ARC_BADGE_LABEL = { member: 'Members', premium: 'Premium', elite: 'Elite' };

  const renderArcCard = (arc, isFirst = false) => {
    const entry = arc.chapters[0];
    const isSelected = arc.chapters.some((c) => c.id === selectedTemplate);
    const isLockedArc = entry.locked;
    const isLastPlayed = !isSelected && !isLockedArc && arc.chapters.some((c) => c.id === lastPlayedTemplateId);
    return (
      <article
        key={arc.id}
        data-testid={`arc-card-${arc.id}`}
        data-tour={isFirst ? 'first-adventure' : undefined}
        className={`arc-card${isSelected ? ' selected' : ''}${isLockedArc ? ' locked' : ''}${entry.comingSoon ? ' soon' : ''}`}
        onClick={() => handleArcCardClick(arc)}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleArcCardClick(arc); }
        }}
      >
        {/* Card art: the entry chapter's card until bespoke arc art lands (phase 2) */}
        <div className="arc-art" style={{ backgroundImage: `url(${arc.art})` }}>
          <span className="arc-span">{arc.chapterCount} {arc.chapterCount === 1 ? 'chapter' : 'chapters'} · Lv {arc.levelSpan[0]}-{arc.levelSpan[1]}</span>
          {isSelected && <span className="price-badge price-badge-gold">Selected</span>}
          {isLastPlayed && <span className="price-badge price-badge-muted">Last played</span>}
          {isLockedArc && <span className="price-badge price-badge-gold">{ARC_BADGE_LABEL[arc.minTierToEnter] || 'Members'}</span>}
        </div>
        <div className="arc-body">
          <div className="arc-title-row">
            <h3>{arc.name}</h3>
            <button type="button" className="inline-link" onClick={(e) => { e.stopPropagation(); openArcModal(arc); }}>Details</button>
          </div>
          <p className="arc-tagline">{arc.tagline}</p>
          {/* Chapter ladder: titles + level bands + status chips, never milestones (decision 1) */}
          <ol className="arc-chapters">
            {arc.chapters.map((chapter) => {
              const chip = chapterChip(chapter, {
                isSignedIn: !!user,
                isRetrying: retryingChapterId === chapter.id,
              });
              return (
                <li key={chapter.id} className={chapter.comingSoon ? 'soon' : ''}>
                  <span className="name">{chapter.subtitle}</span>
                  <span className="chip">{chip.label}</span>
                </li>
              );
            })}
          </ol>
          {!isLockedArc && entry.startable && partyMaxLevel > 0 && entry.levelRange && partyMaxLevel < entry.levelRange[0] && (
            <p className="arc-warn">Needs level {entry.levelRange[0]}+ (your highest: {partyMaxLevel})</p>
          )}
        </div>
      </article>
    );
  };

  // Honest note when a higher-tier (t2/t3) template is SELECTED and the roster
  // can't reach its band yet: same class of soft warning the continue-legend
  // picker uses. Never a block: the real party-level warning (and still no
  // block) happens at hero selection, where the chosen party is known.
  const selectedTemplateData = selectedTemplate && selectedTemplate !== 'custom'
    ? storyTemplates.find((t) => t.id === selectedTemplate)
    : null;
  const higherTierNote = (() => {
    const t = selectedTemplateData;
    if (!t || (t.tier || 1) < 2 || !t.levelRange) return null;
    const effectiveLevel = partyMaxLevel || 1; // no roster yet = a fresh Lv 1 party
    if (effectiveLevel >= t.levelRange[0]) return null;
    const band = `Lv ${t.levelRange[0]}-${t.levelRange[1]}`;
    return isOpeningAccessible(t.settings?.milestones, effectiveLevel)
      ? `This campaign is made for ${band}; your party would start around Lv ${effectiveLevel}. The opening steps are within reach, and rumours in nearby towns will strengthen you for the deeper, level-gated chapters.`
      : `This campaign is made for ${band}; your party would start around Lv ${effectiveLevel}. The opening itself is level-gated; a fresh party will find it brutal, but you may still try.`;
  })();

  const renderTemplateTab = () => (
    <div className="ng-panel">
      <p className="ng-intro">
        Each card is a full campaign. Pick one to start its first chapter; the rest unlock as
        you play. Details lists every chapter.
      </p>
      {waterTownNote && <p className="ng-note">🌊 {waterTownNote}</p>}

      <div className="arc-grid">
        {arcSections.free.map((a, i) => renderArcCard(a, i === 0))}
      </div>

      {arcSections.member.length > 0 && (
        <div className="arc-section" data-testid="member-arcs-section">
          <div className="arc-section-head">
            <h2>Members' campaigns</h2>
            <span className="price-badge price-badge-gold">{premiumUnlocked ? 'Unlocked' : 'Members'}</span>
          </div>
          {!premiumUnlocked && (
            <p className="ng-note">Eldritch, desert and snow campaigns are a Members unlock. Preview them below, or see <Link className="inline-link" to="/membership">Membership</Link>.</p>
          )}
          <div className="arc-grid">
            {arcSections.member.map((a) => renderArcCard(a))}
          </div>
        </div>
      )}

      {arcSections.premium.length > 0 && (
        <div className="arc-section" data-testid="premium-arcs-section">
          <div className="arc-section-head">
            <h2>Premium campaigns</h2>
            <span className="price-badge price-badge-muted">{hasTier('premium') ? 'Unlocked' : 'Premium'}</span>
          </div>
          {!hasTier('premium') && (
            <p className="ng-note">The realm's flagship campaigns, a Premium unlock (coming soon). Preview them below.</p>
          )}
          <div className="arc-grid">
            {arcSections.premium.map((a) => renderArcCard(a))}
          </div>
        </div>
      )}

      {higherTierNote && (
        <div className="app-callout warning" data-testid="higher-tier-note">
          <span><b>Seasoned campaign.</b> {higherTierNote}</span>
        </div>
      )}
    </div>
  );


  // Shared tone settings grid used by both Custom and Freeform tabs
  const renderToneSettings = ({ showThemeReset = false } = {}) => {
    const themeName = THEME_DEFAULTS[customTheme]?.name;
    const anyTouched = Object.keys(toneTouched).length > 0;
    return (
      <div>
        {showThemeReset && (
          <div className="tone-header">
            <span className="tone-header-label">
              Tone &amp; style{themeName ? ` — defaults from ${themeName}` : ''}. Tweak any; your picks stay.
            </span>
            <button
              type="button"
              className="tone-reset-link"
              onClick={() => applyThemeDefaults(customTheme, { force: true })}
              disabled={!anyTouched}
              title={anyTouched ? "Restore this theme's tone defaults" : 'No manual changes to reset'}
            >
              Reset to theme
            </button>
          </div>
        )}
        <div className="tone-dials">
          <SegmentedControl label="Grimness" id="grimness" value={grimnessLevel} options={grimnessOptions} onChange={changeTone('grimness', setGrimnessLevel)} />
          <SegmentedControl label="Darkness" id="darkness" value={darknessLevel} options={darknessOptions} onChange={changeTone('darkness', setDarknessLevel)} />
          <SegmentedControl label="Magic Level" id="magic" value={magicLevel} options={magicOptions} onChange={changeTone('magic', setMagicLevel)} />
          <SegmentedControl label="Technology" id="tech" value={technologyLevel} options={technologyOptions} onChange={changeTone('tech', setTechnologyLevel)} />
          <SegmentedControl label="Narrative Style" id="verbosity" value={responseVerbosity} options={verbosityOptions} onChange={changeTone('verbosity', setResponseVerbosity)} hint="How the AI Dungeon Master paces its narration." />
        </div>
      </div>
    );
  };

  // State for custom configurator
  const [customTheme, setCustomTheme] = useState('heroic-fantasy');
  const [customTier, setCustomTier] = useState(1);

  // Slot state: what the player picks for each of the 4 milestone slots
  const [slot1Item, setSlot1Item] = useState('');
  const [slot1Building, setSlot1Building] = useState('');
  const [slot1Town, setSlot1Town] = useState('');
  const [slot2Role, setSlot2Role] = useState('');
  const [slot2Building, setSlot2Building] = useState('');
  const [slot2Town, setSlot2Town] = useState('');
  const [slot3Poi, setSlot3Poi] = useState('');
  const [slot3Mountain, setSlot3Mountain] = useState('');
  const [slot4Enemy, setSlot4Enemy] = useState('');

  // Build milestones from slot selections
  const buildMilestonesFromSlots = () => {
    // NO literal placeholders may ever reach a map (maintainer 2026-07-06:
    // "Town B" appeared as a real town name). Validation blocks partial slots
    // at preview AND submit, but the boss-only quest legitimately has no
    // region, and defense-in-depth wants every fallback to be a real themed
    // name rather than a placeholder, whatever path anything takes.
    const namePool = THEME_NAMES[customTheme] || THEME_NAMES['heroic-fantasy'];
    const town1 = slot1Town || namePool.towns[0];
    const town2 = slot2Town || namePool.towns[1] || namePool.towns[0];
    const wildLoc = slot3Mountain || namePool.mountains[0];

    // Resolve through the tier/entitlement-filtered list (not the raw catalog view),
    // so a stale or out-of-ceiling item id can never become a milestone spawn.
    const itemData = availableQuestItems.find(i => i.id === slot1Item);
    const buildingData = QUEST_BUILDINGS.find(b => b.id === slot1Building);
    const roleData = NPC_ROLES.find(r => r.id === slot2Role);
    const building2Data = QUEST_BUILDINGS.find(b => b.id === slot2Building);
    const poiData = POI_TYPES.find(p => p.id === slot3Poi);
    const enemyData = slot4Enemy ? QUEST_ENEMIES[slot4Enemy] : null;

    const builtMilestones = [];

    if (itemData && buildingData) {
      builtMilestones.push({
        id: 1,
        text: `Find the ${itemData.name} in the ${buildingData.name.toLowerCase()} at ${town1}`,
        location: town1,
        type: 'item',
        requires: [],
        trigger: { item: slot1Item, action: 'acquire' },
        spawn: { type: 'item', id: slot1Item, name: itemData.name, location: town1 },
        building: { type: slot1Building, name: buildingData.name, location: town1 },
        rewards: { xp: customTier === 1 ? 25 : 100, gold: customTier === 1 ? '1d6' : '2d10', items: [] },
        minLevel: null,
      });
    }

    if (roleData && building2Data) {
      // A `talk` milestone (engine-completed via the Talk button + npc_talked event), NOT
      // `type: 'narrative'`: narrative milestones were AI-judged via a completion marker,
      // which is being retired (the engine referees, the LLM only narrates — #76). The NPC
      // spawn already existed; the trigger.npc just wires it to the deterministic path.
      const slot2NpcId = `quest_npc_${slot2Role.toLowerCase()}`;
      builtMilestones.push({
        id: 2,
        text: `Speak with the ${roleData.name.toLowerCase()} at ${town2}`,
        location: town2,
        type: 'talk',
        requires: [],
        trigger: { npc: slot2NpcId },
        spawn: { type: 'npc', id: slot2NpcId, name: roleData.name, location: town2, role: slot2Role },
        building: { type: slot2Building, name: building2Data.name, location: town2 },
        rewards: { xp: customTier === 1 ? 25 : 150, gold: customTier === 1 ? '1d6' : '1d20', items: [] },
        minLevel: null,
      });
    }

    if (poiData) {
      builtMilestones.push({
        id: 3,
        text: `Reach the ${poiData.name} in the ${wildLoc}`,
        location: wildLoc,
        type: 'location',
        requires: builtMilestones.length >= 2 ? [1, 2] : builtMilestones.length === 1 ? [1] : [],
        trigger: { location: `quest_poi_${slot3Poi}`, action: 'visit' },
        spawn: { type: 'poi', id: `quest_poi_${slot3Poi}`, name: poiData.name, location: wildLoc },
        building: null,
        rewards: { xp: customTier === 1 ? 50 : 200, gold: customTier === 1 ? '1d10' : '3d20', items: [] },
        minLevel: customTier === 1 ? null : customTier + 1,
      });
    }

    if (enemyData) {
      builtMilestones.push({
        id: 4,
        text: `Defeat ${enemyData.name}`,
        location: wildLoc,
        type: 'combat',
        requires: builtMilestones.length >= 3 ? [3] : [],
        trigger: { enemy: slot4Enemy, action: 'defeat' },
        spawn: { type: 'enemy', id: slot4Enemy, name: enemyData.name, location: wildLoc },
        building: null,
        encounter: { ...enemyData },
        rewards: { xp: customTier === 1 ? 50 : 300, gold: customTier === 1 ? '1d10' : '3d20', items: [] },
        // Finale-mirror bosses carry a recommendedLevel (t2 finales are tuned
        // for Lv 5); honor it over the generic tier gate so a custom quest
        // cannot serve a 42%-at-Lv5 boss behind a Lv 4 gate.
        minLevel: Math.max(
          customTier === 1 ? 2 : customTier + 2,
          enemyData.recommendedLevel || 0
        ),
      });
    }

    return builtMilestones;
  };

  // Apply theme defaults when theme changes
  // Apply a theme's tone defaults. By default this only fills dials the player hasn't
  // manually set (sticky overrides win); `force` overrides everything and clears the
  // touched-state (used by the "Reset to theme" link).
  const applyThemeDefaults = (theme, { force = false } = {}) => {
    const defaults = THEME_DEFAULTS[theme];
    if (!defaults) return;
    if (force || !toneTouched.grimness) setGrimnessLevel(defaults.grimnessLevel);
    if (force || !toneTouched.darkness) setDarknessLevel(defaults.darknessLevel);
    if (force || !toneTouched.magic) setMagicLevel(defaults.magicLevel);
    if (force || !toneTouched.tech) setTechnologyLevel(defaults.technologyLevel);
    if (force || !toneTouched.verbosity) setResponseVerbosity(defaults.responseVerbosity);
    if (force) setToneTouched({});
  };

  // Wrap a tone setter so changing it marks that dial "touched" (theme changes then leave it).
  const changeTone = (key, setter) => (val) => {
    setter(val);
    setToneTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  // On a fresh new game, seed the tone dials from the default theme so the Custom tab opens
  // with coherent defaults (Grimness/Darkness aren't blank) rather than nothing selected. Skips
  // when tone was already loaded from a saved game, so it never clobbers restored settings.
  useEffect(() => {
    if (!grimnessLevel && !darknessLevel) {
      applyThemeDefaults(customTheme, { force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate Custom tab slots — returns error string or null if valid
  const validateCustomSlots = () => {
    const slotErrors = [];

    // Slot 1: Item Search — needs item + building + town
    const s1HasAny = slot1Item || slot1Building || slot1Town;
    const s1Complete = slot1Item && slot1Building && slot1Town;
    if (s1HasAny && !s1Complete) {
      const missing = [];
      if (!slot1Item) missing.push('item');
      if (!slot1Building) missing.push('building');
      if (!slot1Town) missing.push('town');
      slotErrors.push(`Slot 1 (Find Quest Item): missing ${missing.join(', ')}`);
    }

    // Slot 2: NPC Conversation — needs role + building + town
    const s2HasAny = slot2Role || slot2Building || slot2Town;
    const s2Complete = slot2Role && slot2Building && slot2Town;
    if (s2HasAny && !s2Complete) {
      const missing = [];
      if (!slot2Role) missing.push('NPC role');
      if (!slot2Building) missing.push('building');
      if (!slot2Town) missing.push('town');
      slotErrors.push(`Slot 2 (NPC Conversation): missing ${missing.join(', ')}`);
    }

    // Slot 3: Location — needs POI + mountain/region
    const s3HasAny = slot3Poi || slot3Mountain;
    const s3Complete = slot3Poi && slot3Mountain;
    if (s3HasAny && !s3Complete) {
      const missing = [];
      if (!slot3Poi) missing.push('point of interest');
      if (!slot3Mountain) missing.push('region');
      slotErrors.push(`Slot 3 (Explore Location): missing ${missing.join(', ')}`);
    }

    // Slot 4: Boss Fight — needs enemy
    // (single field, so it's either complete or empty — no partial state)

    // Floor raised to TWO complete slots (maintainer 2026-07-06: "we should
    // force more than a single milestone though. That isn't particularly
    // interesting."): a one-beat campaign is over before it starts.
    const completeCount = [s1Complete, s2Complete, s3Complete, !!slot4Enemy].filter(Boolean).length;
    if (completeCount < 2) {
      return 'Please complete at least two milestone slots: a single-milestone campaign is over before it starts.';
    }

    if (slotErrors.length > 0) {
      return `Some milestone slots are incomplete:\n${slotErrors.join('\n')}`;
    }

    return null; // valid
  };

  // Sync customNames from selected town/mountain names so map gen picks them up
  useEffect(() => {
    if (activeTab !== 'custom') return;
    const towns = [slot1Town, slot2Town].filter(Boolean);
    const mountains = [slot3Mountain].filter(Boolean);
    // Only update if there are selections — avoid clearing template-set names
    if (towns.length > 0 || mountains.length > 0) {
      setCustomNames({ towns, mountains });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot1Town, slot2Town, slot3Mountain, activeTab]);

  // Sync milestones whenever slot selections change
  useEffect(() => {
    if (activeTab !== 'custom') return;
    const built = buildMilestonesFromSlots();
    if (built.length > 0) {
      setMilestones(built);
      setSelectedTemplate('custom');
      setTemplateLabel('Custom Tale');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot1Item, slot1Building, slot1Town, slot2Role, slot2Building, slot2Town, slot3Poi, slot3Mountain, slot4Enemy, customTier, customTheme, activeTab]);

  // Custom-tab form styles, on the redesign tokens (inline because the slot markup uses them).
  const selectStyle = {
    padding: '.6rem .75rem', borderRadius: '6px', border: '1px solid var(--line)',
    background: 'var(--ground)', color: 'var(--text)', fontSize: '.95rem', width: '100%',
    boxSizing: 'border-box', fontFamily: 'var(--font-ui)',
  };

  const slotLabelStyle = {
    fontSize: '.82rem', color: 'var(--muted)', fontWeight: 600, fontFamily: 'var(--font-ui)',
    display: 'block', marginBottom: '.35rem',
  };

  const slotCardStyle = {
    background: 'var(--surface)', border: '1px solid var(--line-soft)', borderRadius: '10px',
    padding: '1.1rem 1.2rem', marginBottom: '.8rem',
  };

  const availableEnemies = getEnemiesByTierAndTheme(customTier, customTheme);
  // Entitlement-filtered item pool for slot 1: excludes flagship/unobtainable campaign
  // rewards at every tier and caps rarity by campaign tier (tier 3 requires Membership;
  // getCustomQuestItems coerces at the source). Used for the dropdown AND all lookups.
  const availableQuestItems = getCustomQuestItems(customTier);

  // Backstop coercions: state can outlive a lock (e.g. the account tier resolves to
  // 'free' after mount, or a stored selection meets a newly gated catalog). Coerce the
  // stale value out instead of trusting the disabled <option> to have prevented it.
  useEffect(() => {
    if (!canUseTheme(customTheme)) {
      // No campaign genre is premium today (premium ids are world-biome themes), so
      // this is a pure seam guard for when a gated theme is surfaced in this picker.
      setCustomTheme('heroic-fantasy');
    }
  }, [customTheme]);
  useEffect(() => {
    if (slot1Item && !availableQuestItems.some(i => i.id === slot1Item)) {
      setSlot1Item('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot1Item, customTier]);
  useEffect(() => {
    // Same rule as the tab-switch handler, re-checked whenever the tier or theme state
    // settles: a free account must never carry a premium (desert/snow) biome from a
    // previewed template into a Custom/Freeform world.
    if (activeTab === 'custom' && !premiumUnlocked && isThemePremium(worldTheme)) {
      setWorldTheme('grassland');
    }
  }, [activeTab, premiumUnlocked, worldTheme]);

  const renderCustomTab = () => (
    <div className="ng-panel">
      <p className="ng-intro">
        Configure your adventure by selecting ingredients for each quest stage.
      </p>

      {/* Theme + Tier selection */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={slotLabelStyle}>Theme</label>
          <select
            value={customTheme}
            onChange={(e) => {
              const theme = e.target.value;
              // Premium backstop: never let a free user pick a premium (desert/snow) theme.
              // These genre ids aren't premium today, but this keeps the seam airtight if a
              // premium world-biome theme is ever surfaced in this picker.
              if (!premiumUnlocked && isThemePremium(theme)) {
                setFormError('That theme is a Members feature. Open the Membership page to unlock it.');
                return;
              }
              setCustomTheme(theme);
              applyThemeDefaults(theme);
              setSlot4Enemy(''); // Reset enemy when theme changes
              // Auto-populate town/mountain pickers with first names from theme pool
              const names = THEME_NAMES[theme];
              if (names) {
                setSlot1Town(names.towns[0] || '');
                setSlot2Town(names.towns[1] || '');
                setSlot3Mountain(names.mountains[0] || '');
              }
            }}
            style={selectStyle}
          >
            {Object.entries(THEME_DEFAULTS).map(([id, t]) => {
              const locked = !premiumUnlocked && isThemePremium(id);
              return (
                <option key={id} value={id} disabled={locked}>
                  {t.icon} {t.name}{locked ? ' 🔒 (Premium)' : ''}
                </option>
              );
            })}
          </select>
        </div>
        <div style={{ flex: '0 0 200px' }}>
          <label style={slotLabelStyle}>Tier</label>
          {/* Higher custom tiers grey out until the requirements are met
              (maintainer 2026-07-06): Tier 2 wants a Lv 3+ hero on the roster
              (its milestone gates lock a fresh party out mid-campaign anyway);
              Tier 3 wants a Lv 5+ hero AND Membership. The builder's milestone
              minLevel math already scales with the chosen tier. */}
          {(() => {
            const highestLevel = heroes.reduce((m, h) => Math.max(m, h.level || 1), 0);
            // Gates derive from the canonical bands so the custom page can never
            // drift from the ready-made sections again (maintainer 2026-07-06).
            const t2Entry = TIER_LEVEL_BANDS[2][0];
            const t3Entry = TIER_LEVEL_BANDS[3][0];
            const t2Locked = highestLevel < t2Entry;
            const t3Locked = highestLevel < t3Entry || !premiumUnlocked;
            const t3Reason = !premiumUnlocked && highestLevel < t3Entry
              ? `🔒 Members + Lv ${t3Entry} hero`
              : !premiumUnlocked ? '🔒 Members' : `needs a Lv ${t3Entry}+ hero`;
            return (
              <select
                value={customTier}
                onChange={(e) => {
                  setCustomTier(Number(e.target.value));
                  setSlot4Enemy(''); // Reset enemy when tier changes
                }}
                style={selectStyle}
              >
                <option value={1}>Tier 1 ({tierBandLabel(1)})</option>
                <option value={2} disabled={t2Locked}>
                  Tier 2 ({tierBandLabel(2)}){t2Locked ? ` — needs a Lv ${t2Entry}+ hero` : ''}
                </option>
                <option value={3} disabled={t3Locked}>
                  Tier 3 ({tierBandLabel(3)}){t3Locked ? ` — ${t3Reason}` : ''}
                </option>
              </select>
            );
          })()}
        </div>
      </div>

      {/* Optionality was invisible (maintainer 2026-07-06: "Oh, that milestone
          is optional? It isn't obvious at all. That's a hole in the UX.") */}
      <p style={{ margin: '2px 0 10px', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
        Every slot below is optional. Complete the ones you want your quest to
        include (each needs all of its fields filled); at least two full slots
        are required, and empty slots are simply skipped.
      </p>

      {/* Slot 1: Item Search */}
      <div style={slotCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{
            background: 'var(--primary)', color: '#fff', width: 24, height: 24,
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', fontWeight: 'bold',
          }}>1</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text)' }}>📦 Find a Quest Item <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(optional)</span></span>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={slotLabelStyle}>Item</label>
            <RaritySelect
              value={slot1Item}
              onChange={setSlot1Item}
              options={availableQuestItems}
              placeholder="Select item..."
              ariaLabel="Quest item"
              sortByRarity
            />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label style={slotLabelStyle}>Found in</label>
            <select value={slot1Building} onChange={(e) => setSlot1Building(e.target.value)} style={selectStyle}>
              <option value="">Select building...</option>
              {QUEST_BUILDINGS.map(b => (
                <option key={b.id} value={b.id}>{b.icon} {b.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={slotLabelStyle}>Town</label>
            <select value={slot1Town} onChange={(e) => setSlot1Town(e.target.value)} style={selectStyle}>
              <option value="">Select town...</option>
              {(THEME_NAMES[customTheme]?.towns || []).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        {slot1Item && slot1Building && slot1Town && (
          <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Find the {availableQuestItems.find(i => i.id === slot1Item)?.name} in the {QUEST_BUILDINGS.find(b => b.id === slot1Building)?.name.toLowerCase()} at {slot1Town}
          </div>
        )}
      </div>

      {/* Slot 2: NPC Conversation */}
      <div style={slotCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{
            background: 'var(--primary)', color: '#fff', width: 24, height: 24,
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', fontWeight: 'bold',
          }}>2</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text)' }}>💬 Meet an NPC <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(optional)</span></span>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={slotLabelStyle}>NPC Role</label>
            <select value={slot2Role} onChange={(e) => setSlot2Role(e.target.value)} style={selectStyle}>
              <option value="">Select role...</option>
              {NPC_ROLES.map(r => (
                <option key={r.id} value={r.id}>{r.icon} {r.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label style={slotLabelStyle}>Building</label>
            <select value={slot2Building} onChange={(e) => setSlot2Building(e.target.value)} style={selectStyle}>
              <option value="">Select building...</option>
              {QUEST_BUILDINGS.map(b => (
                <option key={b.id} value={b.id}>{b.icon} {b.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <label style={slotLabelStyle}>Town</label>
            <select value={slot2Town} onChange={(e) => setSlot2Town(e.target.value)} style={selectStyle}>
              <option value="">Select town...</option>
              {(THEME_NAMES[customTheme]?.towns || []).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        {slot2Role && slot2Building && slot2Town && (
          <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Speak with the {NPC_ROLES.find(r => r.id === slot2Role)?.name.toLowerCase()} at the {QUEST_BUILDINGS.find(b => b.id === slot2Building)?.name.toLowerCase()} in {slot2Town}
          </div>
        )}
      </div>

      {/* Slot 3: Location / POI */}
      <div style={slotCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{
            background: 'var(--primary)', color: '#fff', width: 24, height: 24,
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', fontWeight: 'bold',
          }}>3</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text)' }}>📍 Reach a Location <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(optional)</span></span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>Requires #1 + #2</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={slotLabelStyle}>Point of Interest</label>
            <select value={slot3Poi} onChange={(e) => setSlot3Poi(e.target.value)} style={selectStyle}>
              <option value="">Select POI...</option>
              {POI_TYPES.map(p => (
                <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={slotLabelStyle}>Region</label>
            <select value={slot3Mountain} onChange={(e) => setSlot3Mountain(e.target.value)} style={selectStyle}>
              <option value="">Select region...</option>
              {(THEME_NAMES[customTheme]?.mountains || []).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        {slot3Poi && slot3Mountain && (
          <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Reach the {POI_TYPES.find(p => p.id === slot3Poi)?.name} in {slot3Mountain}
          </div>
        )}
      </div>

      {/* Slot 4: Boss Fight */}
      <div style={slotCardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{
            background: 'var(--primary)', color: '#fff', width: 24, height: 24,
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.75rem', fontWeight: 'bold',
          }}>4</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--text)' }}>⚔️ Boss Fight <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(optional)</span></span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>Requires #3</span>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={slotLabelStyle}>Enemy (Tier {customTier})</label>
          <select value={slot4Enemy} onChange={(e) => setSlot4Enemy(e.target.value)} style={selectStyle}>
            <option value="">Select boss...</option>
            {availableEnemies.map(e => (
              <option key={e.id} value={e.id}>{e.icon} {e.name} ({e.enemyHP} HP)</option>
            ))}
          </select>
        </div>
        {slot4Enemy && QUEST_ENEMIES[slot4Enemy] && (
          <div style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Defeat {QUEST_ENEMIES[slot4Enemy].name} at the {POI_TYPES.find(p => p.id === slot3Poi)?.name || 'location'} in {slot3Mountain || (THEME_NAMES[customTheme] || THEME_NAMES['heroic-fantasy']).mountains[0]}
          </div>
        )}
      </div>

      {/* Generated milestone summary */}
      {milestones.length > 0 && activeTab === 'custom' && (
        <div style={{
          padding: '12px', background: 'var(--surface)', borderRadius: '8px',
          border: '1px solid var(--border)', marginBottom: '16px',
        }}>
          <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 700 }}>
            Quest Chain Preview
          </div>
          {milestones.map((m, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: 'var(--text)', marginBottom: '3px' }}>
              {milestoneTypeIcon[m.type] || '?'} <strong>#{m.id}</strong> {m.text}
              {m.requires?.length > 0 && <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}> (after #{m.requires.join(', #')})</span>}
            </div>
          ))}
        </div>
      )}

      {renderToneSettings({ showThemeReset: true })}
    </div>
  );


  const dockTemplate = selectedTemplate && selectedTemplate !== 'custom'
    ? storyTemplates.find((t) => t.id === selectedTemplate)
    : null;

  return (
    <div className="rd-page rd-app new-game-page">
      <section className="page-header app-header">
        <div className="wrap">
          <OnboardingSteps currentStep={1} />
          <div className="ng-head">
          <div className="ng-head-text">
          <p className="eyebrow">Step 1 · Choose your adventure</p>
          <h1>Choose your adventure.</h1>
          <p className="lede">Pick a ready-made campaign, or build your own quest from parts.</p>
          {/* Tab navigation */}
          <div className="ng-tabs" role="tablist">
            {[
              { id: 'templates', label: 'Ready-made' },
              { id: 'custom', label: 'Custom' },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={activeTab === tab.id ? 'on' : ''}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'custom' && selectedTemplate && selectedTemplate !== 'custom') {
                    setSelectedTemplate('custom');
                  }
                  // Custom has no biome picker, so a premium world theme here can only be a
                  // leftover from a previewed template. Drop it back to grassland for free users
                  // so they can't ride a premium (desert/snow) map into a custom tale.
                  if (tab.id === 'custom' && !premiumUnlocked && isThemePremium(worldTheme)) {
                    setWorldTheme('grassland');
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          </div>
          {/* Your pick: fills the header's right side and follows the selection. */}
          <div
            className="ng-pick"
            style={{ backgroundImage: `linear-gradient(180deg, rgba(14,13,19,0) 35%, rgba(14,13,19,.9) 100%), url('${dockTemplate ? `/assets/templates/${dockTemplate.id}.webp` : '/assets/redesign/hero.jpg'}')` }}
            aria-live="polite"
          >
            <div className="ng-pick-caption">
              <span className="ng-pick-label">{dockTemplate ? 'Your pick' : (activeTab === 'custom' ? 'Your quest' : 'No campaign picked')}</span>
              <strong>{dockTemplate ? dockTemplate.name : (activeTab === 'custom' ? 'Custom quest' : 'Choose a card below')}</strong>
              {dockTemplate && (
                <span>{dockTemplate.subtitle}{dockTemplate.levelRange ? ` · Lv ${dockTemplate.levelRange[0]}-${dockTemplate.levelRange[1]}` : ''}</span>
              )}
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="band ng-band">
      <div className="wrap">
      {/* Tab Content */}
      {activeTab === 'templates' && renderTemplateTab()}
      {activeTab === 'custom' && renderCustomTab()}

      {/* Arc Detail Modal (chapter ladder + picker, #73 phase 1) */}
      {previewArc && (
        <ArcDetailModal
          arc={previewArc}
          selectedChapterId={previewChapterId}
          onSelectChapter={(id) => { setPreviewChapterId(id); setArcModalNotice(null); }}
          selectedTemplateId={selectedTemplate}
          partyMaxLevel={partyMaxLevel}
          isSignedIn={!!user}
          retryingChapterId={retryingChapterId}
          notice={arcModalNotice}
          onTeaserChapterClick={(chapter) => handleTeaserChapter(chapter, { fromModal: true })}
          onLockedChapterClick={(chapter) => setArcModalNotice(lockedChapterCopy(chapter))}
          onApplyChapter={(template) => {
            // Apply the picked chapter, close the modal, then advance to hero
            // selection once the applied state has flushed (see the
            // pendingCampaignLaunch effect above).
            applyTemplate(template);
            closeArcModal();
            setPendingCampaignLaunch(true);
          }}
          onSubmit={() => {
            closeArcModal();
            // Map is generated automatically in handleSubmit; go straight on.
            setTimeout(() => handleSubmit(), 100);
          }}
          onClose={closeArcModal}
        />
      )}

      {/* World Map Generation Section — hidden for Ready-Made adventures (the map
          is generated automatically on submit); shown for Custom/Freeform. */}
      {activeTab !== 'templates' && (
      <div className="ng-panel ng-map">
        <h2>World map</h2>
        <p>Generate a random world map for your adventure. Each map is unique with forests, mountains, and towns.</p>
        {waterTownNote && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            🌊 {waterTownNote}
          </p>
        )}

        <div className="map-generation-controls">
          <div className="seed-input-group" style={{ marginBottom: '15px' }}>
            <label htmlFor="worldSeed" style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>World Seed:</label>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '10px', flexWrap: 'wrap' }}>
              <input
                id="worldSeed"
                type="number"
                value={worldSeed || ''}
                onChange={(e) => setWorldSeed(e.target.value)}
                placeholder="Leave empty for random"
                style={{ ...selectStyle, width: '200px' }}
              />
              <button
                type="button"
                onClick={() => setWorldSeed(Math.floor(Math.random() * 1000000))}
                className="btn btn-ghost"
              >
                Randomize
              </button>
              {/* Generate sits flush with Randomize, same row + same height (align-items: stretch). */}
              <button
                onClick={() => {
                  // Premium backstop: never build a sand/snow map for a free user.
                  if (!premiumUnlocked && isThemePremium(worldTheme)) {
                    setFormError('Desert and snow maps are a Members feature. Open the Membership page to unlock them, or choose a temperate setting to continue.');
                    return;
                  }
                  // Slot validation BEFORE map generation (maintainer 2026-07-06):
                  // incomplete slots fall back to placeholder names, and a preview
                  // built from them bakes a literal "Town B" into the world.
                  if (activeTab === 'custom') {
                    const slotError = validateCustomSlots();
                    if (slotError) {
                      setFormError(slotError);
                      return;
                    }
                  }
                  // Typed seeds are strings; normalise so the preview is not the seed-42 world.
                  const seedToUse = normalizeWorldSeed(worldSeed) ?? Math.floor(Math.random() * 1000000);
                  setWorldSeed(seedToUse);
                  // Same water-town shims the launch pipeline uses, so the previewed
                  // world is the world the campaign starts on ({} for free tier).
                  const newMap = generateMapData(10, 10, seedToUse, mergeLocationNames(customNames, milestones), worldTheme, waterTownWorldGenOptions(waterTownAccess));
                  setGeneratedMap(newMap);
                  setShowMapPreview(true);
                }}
                className="btn btn-primary"
                type="button"
              >
                {generatedMap ? 'Rebuild map from seed' : 'Generate world map'}
              </button>
            </div>
          </div>

          {generatedMap && (
            <span className="map-status">✓ Map generated!</span>
          )}
        </div>

        {showMapPreview && generatedMap && (
          <div className="map-preview-container">
            <h3>Map Preview</h3>
            <p className="map-preview-hint">
              <strong>Towns:</strong> 🛖 Hamlet | 🏡 Village | 🏘️ Town | 🏰 City<br />
              <strong>Features:</strong> 🌲 Forest | ⛰️ Mountain
            </p>
            <WorldMapDisplay
              mapData={generatedMap}
              playerPosition={(() => {
                try {
                  return findStartingTown(generatedMap);
                } catch (error) {
                  logger.error('Error finding starting town in preview:', error);
                  // Find any town as fallback for preview
                  for (let y = 0; y < generatedMap.length; y++) {
                    for (let x = 0; x < generatedMap[y].length; x++) {
                      if (generatedMap[y][x].poi === 'town') {
                        return { x, y };
                      }
                    }
                  }
                  return { x: 0, y: 0 }; // Last resort
                }
              })()}
              onTileClick={() => { }} // No interaction in preview
              firstHero={null} // No player marker in preview
            />
          </div>
        )}
      </div>
      )}

      </div>
      </section>

      {/* Fixed step dock: what's picked, any error, and the one Next button (the top
          duplicate is gone; full validation still runs in handleSubmit). */}
      <div className="party-dock" role="region" aria-label="Your adventure">
        <div className="wrap party-dock-inner">
          {dockTemplate ? (
            <div className="dock-pick">
              <span className="dock-thumb" style={{ backgroundImage: `url('/assets/templates/${dockTemplate.id}.webp')` }} aria-hidden="true" />
            </div>
          ) : null}
          <div className="party-dock-text">
            {formError
              ? <span className="party-error" role="alert">{formError}</span>
              : dockTemplate
                ? <span><b>{dockTemplate.name}</b>{dockTemplate.subtitle ? ` · ${dockTemplate.subtitle}` : ''}</span>
                : activeTab === 'custom'
                  ? <span><b>Custom quest</b> · fill at least two slots</span>
                  : <span>Pick a campaign to continue</span>}
          </div>
          <button onClick={handleSubmit} className="btn btn-primary party-start" data-tour="newgame-submit" disabled={activeTab === 'templates' && !dockTemplate}>
            Next: Select Heroes
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewGame;


// const { state } = useLocation();
// const settingsData = state?.settingsData;

/*
const [shortDescription, setShortDescription] = useState(settingsData?.shortDescription || '');
const [grimnessLevel, setGrimnessLevel] = useState(settingsData?.grimnessLevel || '');
const [darknessLevel, setDarknessLevel] = useState(settingsData?.darknessLevel || '');
const [magicLevel, setMagicLevel] = useState(settingsData?.magicLevel || 'Low Magic');
const [technologyLevel, setTechnologyLevel] = useState(settingsData?.technologyLevel || 'Medieval');
const [responseVerbosity, setResponseVerbosity] = useState(settingsData?.responseVerbosity || 'Moderate');
*/
